import { GoogleOAuthProvider } from "@react-oauth/google";
import useServerTime from "@repo/hooks/utils/useServerTime";
import type { ICustomerQuery } from "@repo/shopify/src/queries/customer-gql";
import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import Button from "@components/atoms/Button";
import Dim from "@components/atoms/Dim";
import Icon from "@components/atoms/Icon";
import PageTitle from "@components/atoms/PageTitle";

import EmailLogin from "@organisms/Login/EmailLogin";
import GoogleLogin from "@organisms/Login/GoogleLogin";
import KakaoLogin from "@organisms/Login/KakaoLogin";
import VerifyAccount from "@organisms/Login/VerifyAccount";

import useLayout from "@hooks/reducerHooks/useLayout";
import useUser from "@hooks/reducerHooks/useUser";

import { IconName } from "@const/enum";
import { URLS } from "@const/urls";

import { checkAccessAllowed } from "@utils/archive-sale";
import { NOTIFICATIONS } from "@utils/notifications";

const STEP = {
  LOGIN: "login",
  EMAIL_LOGIN: "email_login",
  CERTIFICATION: "certification",
};

function LoginModal() {
  const location = useLocation();
  const navigate = useNavigate();

  const { accessToken, customer, userLogout } = useUser();
  const {
    closeLoginModal,
    isLoginOpen,
    loginModalProps: { redirectUrl, callback },
    openToast,
    openModal,
    closeModal,
  } = useLayout();
  const { t } = useTranslation();
  const serverTime = useServerTime();

  /** 로그인 단계 */
  const [step, setStep] = useState(
    accessToken && customer ? ((customer.phone && customer.birth?.value) ?? STEP.CERTIFICATION) : STEP.LOGIN,
  );

  /** 모달 닫기 버튼 클릭 이벤트 핸들러 */
  const handleClose = useCallback(() => {
    isLoginOpen && closeLoginModal();
  }, [isLoginOpen, closeLoginModal]);

  /** 로그인 완료되었을 때 본인인증 여부 확인 */
  const onCompleteLogin = (_accessToken?: string, _customer?: ICustomerQuery["customer"]) => {
    const isAllowed = checkAccessAllowed({ customer, currentTime: serverTime });
    if (!isAllowed) {
      userLogout();
      navigate(URLS.HOME);

      openModal({
        title: "TUNE’s Archive Sale",
        description:
          "본 페이지는 TUNE 멤버십 Black / Platinum / Gold / Silver 회원을 대상으로 사전 진행되며, 이외 등급은 10월 30일 13시부터 접속 가능합니다.",
        confirmButton: {
          label: "Okay",
          onClick: () => {
            setStep(STEP.LOGIN);
            userLogout();
            navigate(URLS.HOME);

            closeModal();
          },
        },
      });

      return;
    }

    // 로그인일 때 userInfo phone 전화번호 있는지 확인.
    if (!customer.phone || !customer.birth?.value) {
      // phone 없을 경우 verify 단계로 변경
      openToast(NOTIFICATIONS.SUCCESS_LOGIN);
      setStep(STEP.CERTIFICATION);
    } else {
      // phone 있을 경우 로그인 모달 닫기
      closeLoginModal();
      navigate(location.state?.redirectUrl ?? redirectUrl ?? URLS.ACCOUNT_ORDER_HISTORY, { replace: true });
    }

    callback(_accessToken, _customer);
  };

  return (
    <>
      <div className="w-mobileModal rounded-2.5 border-tuneGrey-200 pc:w-1/4 pc:max-w-120 fixed top-1/2 left-1/2 z-40 flex -translate-x-1/2 -translate-y-1/2 flex-col gap-5 border bg-white p-5 text-center">
        {/* 닫기 버튼 */}
        <Button className="absolute top-5 right-5" onClick={handleClose} styleType="secondary" variant="containedIcon">
          <Icon name={IconName.Close_100} />
        </Button>

        <div className="my-7.5">
          <PageTitle className="mb-5" title={step === STEP.CERTIFICATION ? "VERIFY\nACCOUNT" : "WELCOME\nBACK"} />
          <div className="flex flex-col gap-2.5">
            {/* step 로그인 */}
            {step === STEP.LOGIN && (
              <>
                <KakaoLogin onComplete={onCompleteLogin} />
                <GoogleOAuthProvider clientId={import.meta.env.VITE_REACT_APP_GOOGLE_OAUTH_CLIENT_ID as string}>
                  <GoogleLogin onComplete={onCompleteLogin} />
                </GoogleOAuthProvider>
                <Button
                  onClick={() => {
                    setStep(STEP.EMAIL_LOGIN);
                  }}
                  styleType="secondary"
                  variant="social"
                >
                  <span className="flex w-full items-center justify-center gap-[8px]">
                    <Icon alt="email" className="size-[14px]" name={IconName.Email_100} />
                    <span>{t("login.email_continue")}</span>
                  </span>
                </Button>
              </>
            )}
            {/* step 본인인증 */}
            {step === STEP.CERTIFICATION && (
              <div>
                <VerifyAccount
                  onComplete={() => {
                    callback();
                  }}
                  redirectUrl={redirectUrl}
                />
              </div>
            )}
            {/* step 이메일 로그인 */}
            {step === STEP.EMAIL_LOGIN && (
              <>
                <EmailLogin onComplete={onCompleteLogin} />
                <div className="mt-2.5 flex justify-center">
                  <Button
                    className="text-tuneGrey-400 text-sm underline"
                    onClick={() => {
                      setStep(STEP.LOGIN);
                    }}
                    variant="text"
                  >
                    {t("login.social_continue")}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      <Dim onClick={handleClose} />
    </>
  );
}

export default LoginModal;
